import { DependencyList, useEffect, useRef } from 'react';

const DEFAULT_WAIT = 500;

/**
 * Debounced `useEffect`.
 *
 * The effect will be executed in a debounced manner.
 *
 * @param factory the function to run
 * @param deps the dependency array
 * @param wait the wait time for the debounced behavior
 */
export const useDebouncedEffect = <T>(
  factory: () => T,
  deps: DependencyList | undefined,
  wait: number = DEFAULT_WAIT,
) => {
  const timeout = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (timeout.current) {
      clearTimeout(timeout.current as number);
    }

    timeout.current = setTimeout(() => {
      factory();
    }, wait) as unknown as number;

    // Without this the last run outlives the component: a factory scheduled inside the wait still
    // fires after unmount and does its work for a tree that is gone - which for the mask preview
    // means a run-length decode and a canvas the size of the whole cropped mask. A dependency
    // change already clears the pending run above; unmount is the case with nothing to clear it.
    return () => {
      if (timeout.current) {
        clearTimeout(timeout.current as number);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};
